import { ListSearchFilterCustomProps } from "@pixel/core";
import { ListAppliedSearchFilterProps, ListAppliedSearchFilterValueProps, ChangeAppliedFilterCustomCallback } from "@pixel/core/ListSearchFilter";

interface NameString {
    name: string;
}

export interface IODSActions {
    addSubBu: string
    duplicate: string
    edit: string
    makeActive: string
    makeInactive: string
    delete?: string
}

export interface IBreadcrumbHierarchyObject {
    eid: string
    name: string
}

export interface IBusinessData {
    eid: string
    name: string
    subBusinessUnitsCount: number
    active: boolean
    description: string
    ParentBusinessUnit?: string
    actions: IODSActions
    ancestors?: Array<IBreadcrumbHierarchyObject>
    subChildrenCount: number
}

export interface IBusinessResultData {
    nodes: Array<IBusinessData>
    pageSize: number
    totalCount?: number
    pageNumber: number
}

export interface IBusinessGetParms {
    parentEId?: string
    pageSize?: number
    pageNumber: number
    status?: string
}

export interface IBusinessUnitAPIResponse {
    businessUnits: Array<IBusinessData>;
    totalCount: number;
    pageSize: number;
    pageNumber: number;
}

export interface IBUSearchGetParms {
    pageSize?: number
    pageNumber?: number
    keywords?: string
}

interface ICallback {
    (): void;
}

export interface IAddBusinessUnitPayload {
    parentEId?: string
    eid?: string
    name?: string
    description?: string
    emailLogoUrl?: string
    socialMediaLogoUrl?: string
    twitterLogoUrl?: string
    careerSiteUrl?: string
    privacyNoticeUrl?: string
    parentName?: string
    active?: boolean
    actions?: IODSActions
}

// Departments Interface
export interface IDepartmentsData {
    eid?: string
    name?: string
    description?: string
    subDepartmentsCount?: number
    active?: string
    ancestors?: Array<IBreadcrumbHierarchyObject>
    createdBy?: string
    createdDate?: Date
    actions?: IODSActions
    subChildrenCount?: number
}

export interface IDepartmentsGetParms {
    parentEId?: string
    pageSize?: number
    pageNumber: number
    status?: string
}

export interface IDepartmentsSearchGetParms {
    pageSize?: number
    pageNumber?: number
    keywords?: string
}

export interface IDepartmentsResultData {
    nodes: Array<IDepartmentsData>
    pageSize: number
    totalCount?: number
    pageNumber: number
}

export interface IDepartmentAPIResponse {
    departments: Array<IDepartmentsData>;
    totalCount: number;
    pageSize: number;
    pageNumber: number;
    activeDepartments: Array<IDepartmentsData>;
}

export interface IAddDepartmentPayload {
    parentEId?: string
    eid?: string
    name?: string
    description?: string
    parentName?: string
    active?: boolean
    createdBy?: string
    createdDate?: Date
    actions?: IODSActions
}

// location interface
export interface ILocationsData {
    eid?: string
    name?: NameString
    subLocationsCount?: number
    active?: string
    ancestors?: Array<IBreadcrumbHierarchyObject>
    createdDate?: Date
    createdById?: string
    createdByUser?: string
    status?: string
    actions?: IActionsLoc
}

export interface IAccessGroupLocationsData {
    eid?: string
    name?: NameString
    subChildrenCount?: number
    ancestors?: Array<IBreadcrumbHierarchyObject>
}

export interface ILocationsResultData {
    nodes: Array<ILocationsData> | Array<IAccessGroupLocationsData>
    pageSize: number
    totalCount?: number
    pageNumber: number
}

export interface ILocationsSearchGetParms {
    pageSize?: number
    pageNumber?: number
    keywords?: string
}

export interface ILocationsGetParms {
    parentEId?: string
    pageSize?: number
    pageNumber: number
    status?: string
}

export interface ILocationsFilterGetParms {
    status?: string
    createdByEIds?: string
    pageSize?: number
    pageNumber?: number
}

export interface ILocationAPIResponse {
    locations: Array<ILocationsData>
    totalCount?: number
    pageSize: number
    pageNumber: number
}

export interface IActionsLoc {
    addSubLoc: string
    duplicate: string
    edit: string
    makeActive: string
    makeInactive: string
    delete?: string
}

export interface ICountrySelect {
    country: string
    threeLetterAcronym: string
    twoLetterAcronym: string
}

export interface IStateSelect {
    key: string
    value: string
}

export interface ITimeZone {
    id: number
    localizedName: string
    name: string
    systemId: string
}

export interface ILocationDetailPayload {
    eid?: string
    name?: string
    city?: string
    state?: string
    country?: string
    postalCode?: string
    address?: string
    address2?: string
    description?: string
    office?: boolean
    remote?: boolean
    jobTimeZoneId?: number
    language?: string
    shortDateFormat?: string
    longDateFormat?: string
    shortTimeFormat?: string
    longTimeFormat?: string
    numberFormatLocale?: string
    parentName?: string
    parentEId?: string
    active?: boolean
    createdDate?: Date
    createdByUser?: string
    createdById?: string
    actions?: IActionsLoc
    subLocationsCount?: number
}

// AccessGroups Interface
export interface IAccessGroupsData {
    eid?: string
    name?: string
    code?: string
    companyId?: number
    description?: string
    userCount?: number
    active?: boolean
    actions?: IODSActions
    status?: string
    createdDate?: Date
    createdByUser?: string
}

export interface IAccessGroupsResultData {
    nodes: Array<IAccessGroupsData>
    pageSize: number
    totalCount?: number
    pageNumber: number
}

export interface IAccessGroupsAPIResponse {
    accessGroups: Array<IAccessGroupsData>;
    totalCount: number;
    pageSize: number;
    pageNumber: number;
}

export interface IAccessGroupsGetParms {
    parentEId?: string
    pageSize?: number
    pageNumber: number
}

export interface IAccessGroupsSearchGetParms {
    pageSize?: number
    pageNumber?: number
    keywords?: string
}

export interface IFetchAccessGroupPayload {
    eid?: string
    name?: string
    description?: string
    parentName?: string
    active?: boolean
    createdBy?: string
    createdDate?: Date
    actions?: IODSActions
}

export interface IAccessGroupEmployee {
    eid: string,
    selected: boolean
}

export interface ISaveAccessGroupPayload {
    name?: string
    description?: string
    code?: string
    status?: string
    createdBy?: string
    createdDate?: Date
    accessGroupUserSelectionList?: Array<IAccessGroupEmployee>
    accessGroupLocationSelectionList?: Array<IDimensionsAPIPayload>
}

// users types
export interface IUser {
    eid: string
    name?: string
    otherAccessGroups?: Array<string>
    roles?: Array<string>
}

export interface IUsersData {
    nodes?: Array<IUser>
    totalCount?: number
    pageSize?: number
    pageNumber?: number
}

export interface IUsersAPIResponse {
    users: Array<IUser>;
    totalCount: number;
    pageSize: number;
    pageNumber: number;
}

export interface IUsersGetParms {
    pageSize?: number
    pageNumber: number
}

export interface IUsersSearchGetParms {
    pageSize?: number
    pageNumber?: number
    keywords?: string
}

export interface IDimensionsAPIPayload {
    eid: string,
    associationType: string
}

export interface ISelectedData {
    eid?: string,
    name?: NameString,
}

export interface IStoredDimension {
  selectionCount: number;
  partialSelectedCount: number;
  totalCount: number;
  parentEId: string | null;
  associationType: string;
  level?: number,
}

export interface IStoredMappingData {
    dimensionIdToDimensionList: {
        [key: string]: IStoredDimension
    },
    adjancencyList: {
        [key : string]: Array<string>;
    }

  }

export interface ISelectedDimensionsData {
    eid?: string,
    associationType?: AssociationType
    parentEId?: string,
    selectionCount?: number,
    totalCount?: number,
    level?: number,
    partialSelectedCount?: number,
}

export interface IDimensionsProps {
    accessGroupData: any
    selectedLocationForAPIPayload?: Array<ISelectedDimensionsData>
    setSelectedLocationForAPIPayload?: any
    selectedDepartmentForAPIPayload?: Array<ISelectedDimensionsData>
    setSelectedDepartmentForAPIPayload?: any
    selectedBusinessUnitForAPIPayload?: Array<ISelectedDimensionsData>
    setSelectedBusinessUnitForAPIPayload?: any
    setCurrentDimensionSelectionList: React.Dispatch<React.SetStateAction<{}>>
}

export interface AddDimensionModalProps {
    modalState: boolean;
    setAddDimensionModalState: any;
    addSelectedDimensions: any;
    dimension: string;
    accessDrawerData: any;
    selectedLocationForAPIPayload?: Array<ISelectedDimensionsData>
    setSelectedLocationForAPIPayload?: any
    selectedDepartmentForAPIPayload?: Array<ISelectedDimensionsData>
    setSelectedDepartmentForAPIPayload?: any
    selectedBusinessUnitForAPIPayload?: Array<ISelectedDimensionsData>
    setSelectedBusinessUnitForAPIPayload?: any
    showInfoMessage: IDimensionInfoMessage;
    setShowInfoMessage: React.Dispatch<React.SetStateAction<IDimensionInfoMessage>>;
}

export enum AssociationType {
    NOT_ASSOCIATED = 'NOT_ASSOCIATED',
    PARTIALLY_ASSOCIATED = 'PARTIALLY_ASSOCIATED',
    FULLY_ASSOCIATED = 'FULLY_ASSOCIATED',
};

export interface IDimension {
    eid: string;
    name: string;
}

export interface ILocations {
    locations: Array<IDimension>;
    locationsCount: number;
}

export interface IDepartments {
    departments: Array<IDimension>;
    departmentsCount: number;
}

export interface IBusinessUnits {
    businessUnits: Array<IDimension>;
    businessUnitsCount: number;
}

export interface IDimensionDropdownListProps {
    url: string;
    page: number;
    keywords: string;
    onNewResults?: (response: ILocations | IDepartments | IBusinessUnits) => void;
    onNextPageResults?: (response:  ILocations | IDepartments | IBusinessUnits) => void;
    onError?: (response: any) => void;
}

export interface IDimensionsDropdownProps {
    searchFilter: ListSearchFilterCustomProps;
    onChangeAppliedFilter: ChangeAppliedFilterCustomCallback;
    appliedSearchFilter: ListAppliedSearchFilterProps;
}

export interface IDimensionOption {
    label: string;
    value: string;
}

export interface IAccessGroupFilter {
    locationsFilter:Array<ListAppliedSearchFilterValueProps>;
    departmentsFilter: Array<ListAppliedSearchFilterValueProps>;
    businessUnitsFilter: Array<ListAppliedSearchFilterValueProps>;
    status: string;
}

export interface INamedDimensionData {
    eid: string;
    name: string;
    level: number;
    preSelected: boolean;
}

export interface IDimensionInfoMessage {
    locationInfoMessage: boolean;
    departmentInfoMessage: boolean;
    businessUnitInfoMessage: boolean;
}